import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  COUNTRY_CODES,
  SPECIFIC_ERROR_CODES,
} from '@savgroup-front-common/constants';
import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';

import { SellerService } from '@/api';

import { PickedCompany } from '../../../OnboardingActivities/OnboardingActivities.types';
import { getIsInvalidWebsiteError } from '../helpers/getIsInvalidWebsiteError';
import {
  UPSERT_SALES_LEAD_RESULT,
  UpsertSalesLeadResult,
} from '../OnboardingForm.constants';

import messages from './messages';
import { OnboardingFormFieldValues } from './useOnboardingFormContext';

interface UpsertSalesLeadArgs extends OnboardingFormFieldValues {
  pickedCompany?: PickedCompany;
}

const useUpsertSalesLeadCommand = () => {
  const navigate = useNavigate();
  const locale = useSelector(selectLocale);

  const { mutateAsync: handleSubmitSalesLead, isLoading } = useMutation(
    [],
    async (data: UpsertSalesLeadArgs): Promise<UpsertSalesLeadResult> => {
      const website = data.website?.trim() || undefined;

      const response = await SellerService.upsertSalesLeadCommand({
        companyName: data.companyName,
        email: data.email,
        firstName: data.firstName,
        cName: data.cName,
        lastName: data.lastName,
        phone: data.phone,
        language: locale,
        website,
        address: {
          address: data.address,
          postalCode: data.postalCode,
          city: data.city,
          countryCode: data.countryCode.value as COUNTRY_CODES,
        },
      });

      if (
        response.errors?.at(0)?.code ===
        SPECIFIC_ERROR_CODES.INVALID_EMAIL_ADDRESS
      ) {
        toast.error(messages.invalidEmail);

        return UPSERT_SALES_LEAD_RESULT.INVALID_EMAIL;
      }

      if (getIsInvalidWebsiteError(response.errors?.at(0))) {
        return UPSERT_SALES_LEAD_RESULT.INVALID_WEBSITE;
      }

      if (response.failure) {
        toast.error(messages.accoundCreationError);

        return UPSERT_SALES_LEAD_RESULT.ERROR;
      }

      navigate(`/onboarding/${response.value}`, {
        state: {
          pickedCompany: data.pickedCompany,
          companyName: data.companyName,
        },
      });

      return UPSERT_SALES_LEAD_RESULT.SUCCESS;
    },
  );

  return { handleSubmitSalesLead, isLoading };
};

export default useUpsertSalesLeadCommand;
